/**
 * Target image dimensions extraction for pricing formulas.
 *
 * Both the UI cost preview (ImageGenerationContext) and backend pricing (TokenService)
 * must agree on how target dimensions are read from widget payloads / input parameters.
 * Divergence causes UI/backend price mismatch or silent under-pricing at debit time.
 *
 * Priority order:
 *   1. Top-level `width` / `height`
 *   2. `size_width` / `size_height` (text2image convention)
 *   3. `image_size` as JSON string or object `{ width, height }`
 *   4. Per-field `${field_id}_width` / `${field_id}_height` — ONLY when
 *      `dynamicWidget` is provided AND `field_id` is an `image_size` widget field.
 *
 * Both the top-level fallbacks and the per-field fallback are intentionally narrow.
 * We do NOT scan arbitrary keys for JSON-stringified `{width,height}` payloads,
 * because MCP callers can inject arbitrary values under open `Record<string, unknown>`
 * inputs (see GetPricingInputSchema), which would let them silently price 1×1 images
 * or override real image_size data. Same for arbitrary `*_width`/`*_height` pairs —
 * input-video widgets emit `video_width`/`video_height` as input metadata and would
 * be misread as target dims.
 */
import type { ImageDimensions } from '@functions/utils/formula-evaluator';

/**
 * Minimal structural shape needed for the per-field fallback. Accepts both
 * `DynamicWidgetConfig` (Zod-parsed, backend) and the frontend's locally-defined
 * `EmbeddedDynamicWidgetConfig` — both expose the same shape with `fields[].field_type`
 * as a string union.
 */
export interface TargetDimensionsWidgetConfig {
  fields?: ReadonlyArray<{ field_id: string; field_type: string }>;
}

function parseMaybeJSON(value: unknown): Record<string, unknown> | null {
  if (value && typeof value === 'object') return value as Record<string, unknown>;
  if (typeof value === 'string') {
    try {
      const parsed = JSON.parse(value);
      if (parsed && typeof parsed === 'object') return parsed as Record<string, unknown>;
    } catch {
      /* not JSON */
    }
  }
  return null;
}

/**
 * Extract target image dimensions from widget payload / input parameters.
 *
 * @param inputParameters Flattened widget payload (key → value).
 * @param dynamicWidget Optional workflow widget config. When provided, enables the
 *                     image_size per-field `_width`/`_height` fallback.
 */
export function extractTargetDimensions(
  inputParameters?: Record<string, unknown> | null,
  dynamicWidget?: TargetDimensionsWidgetConfig | null
): ImageDimensions | null {
  if (!inputParameters) return null;

  // 1. Direct width/height
  if (inputParameters.width && inputParameters.height) {
    return { width: Number(inputParameters.width), height: Number(inputParameters.height) };
  }

  // 2. size_width/size_height (common in text2image workflows)
  if (inputParameters.size_width && inputParameters.size_height) {
    return { width: Number(inputParameters.size_width), height: Number(inputParameters.size_height) };
  }

  // 3. `image_size` key (JSON string or object). Intentionally scoped to the
  //    literal `image_size` key — scanning arbitrary keys is unsafe when callers
  //    (e.g. MCP `inputs: Record<string, unknown>`) can inject `{ foo: {width,height} }`.
  if (inputParameters.image_size) {
    const parsed = parseMaybeJSON(inputParameters.image_size);
    if (parsed && parsed.width && parsed.height) {
      return { width: Number(parsed.width), height: Number(parsed.height) };
    }
  }

  // 4. Per-field `${field_id}_width` / `${field_id}_height` — only for known
  //    image_size fields. Without widget context we skip this (would otherwise
  //    pick up input-media widgets' `video_width`/`video_height`). Scoping to
  //    declared image_size fields also stops MCP callers from spoofing target
  //    dims via arbitrary `foo_width`/`foo_height` pairs.
  if (dynamicWidget?.fields?.length) {
    for (const field of dynamicWidget.fields) {
      if (field.field_type !== 'image_size') continue;
      const widthKey = `${field.field_id}_width`;
      const heightKey = `${field.field_id}_height`;
      const widthVal = inputParameters[widthKey];
      const heightVal = inputParameters[heightKey];
      if (widthVal && heightVal) {
        return { width: Number(widthVal), height: Number(heightVal) };
      }
    }
  }

  return null;
}
